import { assign, Machine } from '@xstate/compiled';

interface Context {
  balanceChanges: {
    [bankAccountId: string]: { change: string; actualBalance: number };
  };
}

type Event =
  | {
      type: 'EDIT_BALANCE';
      change: string;
      bankAccountId: string;
      actualBalance: number;
    }
  | {
      type: 'SAVE_CHANGES';
    }
  | {
      type: 'CLEAR_CHANGES';
    };

export const updateModelBalancesMachine = Machine<
  Context,
  Event,
  'updateModelBalances'
>(
  {
    initial: 'idle',
    context: {
      balanceChanges: {},
    },
    states: {
      idle: {
        meta: {
          description: `Awaiting user input - the DB and context are in sync`,
        },
        on: {
          EDIT_BALANCE: {
            actions: 'assignNewBalanceToContext',
          },
          SAVE_CHANGES: {
            target: 'checkingForErrors',
          },
          CLEAR_CHANGES: {
            target: 'clearing',
          },
        },
      },

      checkingForErrors: {
        always: [
          {
            cond: 'aBalanceIsNotANumber',
            target: 'errored.aBalanceIsNotANumber',
          },
          { target: 'saving' },
        ],
      },

      clearing: {
        invoke: {
          src: 'deleteModelBalances',
          onDone: {
            target: 'idle',
            actions: ['onSuccess'],
          },
          onError: {
            target: 'idle',
            actions: ['onError'],
          },
        },
      },

      saving: {
        meta: {
          description: `Saving the changes to the database`,
        },

        invoke: {
          src: 'saveModelBalances',
          onDone: {
            target: 'idle',
            actions: ['onSuccess'],
          },
          onError: {
            target: 'idle',
            actions: ['onError'],
          },
        },
      },

      errored: {
        on: {
          EDIT_BALANCE: {
            actions: 'assignNewBalanceToContext',
            target: 'checkingForErrors',
          },
        },
        states: {
          aBalanceIsNotANumber: {},
        },
      },
    },
  },
  {
    actions: {
      assignNewBalanceToContext: assign((context, event) => {
        return {
          balanceChanges: {
            ...context.balanceChanges,
            [event.bankAccountId]: {
              change: event.change,
              actualBalance: event.actualBalance,
            },
          },
        };
      }),
    },
    guards: {
      aBalanceIsNotANumber: (context) => {
        return Object.values(context.balanceChanges).some(({ change }) =>
          isNaN(Number(change)),
        );
      },
    },
  },
);
